import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline as DeleteOutlineIcon,
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon,
  PhotoCamera as PhotoCameraIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  LogoChange,
  validateLogoFile,
} from "../../utils/organizationLogo.helpers";

const PREVIEW_SIZE = 72;

const ACCEPT_MIME =
  "image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg";

export type OrganizationLogoManagerHandle = {
  getLogoChange: () => LogoChange;
  resetLogoChange: () => void;
};

interface OrganizationLogoManagerProps {
  logoUrl: string | null;
  organizationId?: string;
  disabled?: boolean;
  /** Fires whenever the pending logo selection changes. */
  onLogoChange?: (change: LogoChange) => void;
}

const OrganizationLogoManager = forwardRef<
  OrganizationLogoManagerHandle,
  OrganizationLogoManagerProps
>(function OrganizationLogoManager(
  {
    logoUrl,
    organizationId: _organizationId,
    disabled = false,
    onLogoChange,
  },
  ref
) {
  const theme = useTheme();
  const [logoChange, setLogoChange] = useState<LogoChange>("unchanged");
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null
  );
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logoChangeRef = useRef(logoChange);
  logoChangeRef.current = logoChange;
  const onLogoChangeRef = useRef(onLogoChange);
  onLogoChangeRef.current = onLogoChange;

  useEffect(() => {
    onLogoChangeRef.current?.(logoChange);
  }, [logoChange]);

  const pendingFile =
    logoChange !== "unchanged" && logoChange !== "removed"
      ? logoChange.file
      : null;

  const hasLogo =
    logoChange === "removed"
      ? false
      : Boolean(pendingFile) || Boolean(logoUrl);

  const previewUrl =
    pendingPreviewUrl ??
    (logoChange === "removed" ? null : logoUrl);

  const filename = pendingFile?.name;

  useEffect(() => {
    if (!pendingFile) {
      setPendingPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPendingPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  useImperativeHandle(
    ref,
    () => ({
      getLogoChange: () => logoChangeRef.current,
      resetLogoChange: () => {
        setLogoChange("unchanged");
        setError(null);
      },
    }),
    []
  );

  const assignFile = (file: File | undefined) => {
    if (!file || disabled) return;
    const validationError = validateLogoFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setLogoChange({ file });
  };

  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    assignFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    assignFile(e.dataTransfer.files?.[0]);
  };

  const removeLogo = () => {
    if (pendingFile) {
      setLogoChange(logoUrl ? "removed" : "unchanged");
      return;
    }
    if (logoUrl) {
      setLogoChange("removed");
    }
  };

  const inputId = `org-logo-upload-${_organizationId ?? "new"}`;

  const previewSx = {
    width: PREVIEW_SIZE,
    height: PREVIEW_SIZE,
    flexShrink: 0,
    borderRadius: 1.5,
    border: "1px solid",
    borderColor: dragOver
      ? "primary.main"
      : hasLogo
        ? alpha(theme.palette.success.main, 0.4)
        : "divider",
    bgcolor: dragOver
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.action.hover, 0.04),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "border-color 0.15s, background-color 0.15s",
    position: "relative" as const,
    opacity: disabled ? 0.6 : 1,
    "&:hover": disabled
      ? undefined
      : {
          borderColor: "primary.main",
          bgcolor: alpha(theme.palette.primary.main, 0.06),
        },
  };

  return (
    <Stack spacing={1.25}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
      >
        Organization logo
      </Typography>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ borderRadius: 1.5, py: 0 }}
        >
          {error}
        </Alert>
      )}

      <input
        id={inputId}
        type="file"
        hidden
        accept={ACCEPT_MIME}
        disabled={disabled}
        onChange={handlePickFile}
      />

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ minHeight: PREVIEW_SIZE }}
      >
        <Box
          component="label"
          htmlFor={disabled ? undefined : inputId}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={disabled ? undefined : handleDrop}
          sx={previewSx}
        >
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Organization logo"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                p: 0.75,
              }}
            />
          ) : (
            <ImageIcon sx={{ fontSize: 28, color: "text.disabled" }} />
          )}
        </Box>

        <Stack spacing={0.5} flex={1} minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block">
            PNG or JPEG, max 2 MB · click or drag onto the preview
          </Typography>

          {hasLogo && filename && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={filename}
              sx={{ maxWidth: "100%" }}
            >
              {filename}
            </Typography>
          )}

          {!disabled && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
              sx={{ pt: 0.25 }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhotoCameraIcon sx={{ fontSize: 16 }} />}
                onClick={() => document.getElementById(inputId)?.click()}
                sx={{
                  textTransform: "none",
                  minHeight: 28,
                  py: 0.25,
                  px: 1.25,
                  fontSize: "0.8125rem",
                }}
              >
                {hasLogo ? "Replace" : "Upload"}
              </Button>

              {previewUrl && logoUrl && logoChange === "unchanged" && (
                <Tooltip title="Open in new tab">
                  <IconButton
                    size="small"
                    href={logoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                    sx={{ color: "text.secondary" }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}

              {hasLogo && (
                <Tooltip title="Remove logo">
                  <IconButton
                    size="small"
                    onClick={removeLogo}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});

export default OrganizationLogoManager;
